import { createContext, useCallback, useMemo, useState, type ReactNode } from 'react';

export interface FullscreenContextValue {
  isFullscreen: boolean;
  setFullscreen: (value: boolean) => void;
  toggleFullscreen: () => void;
}

/**
 * Optional override for `useAnnotationFullscreen`. When a `LocalFullscreenProvider`
 * sits above a consumer, the hook returns this scoped state instead of the
 * global comments-store value. Used by the compare view to give each pane its
 * own fullscreen toggle without crossing wires through the shared store.
 */
export const FullscreenContext = createContext<FullscreenContextValue | null>(null);

interface ProviderProps {
  children: ReactNode;
}

export function LocalFullscreenProvider({ children }: ProviderProps) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const toggle = useCallback(() => setIsFullscreen((prev) => !prev), []);
  const value = useMemo<FullscreenContextValue>(
    () => ({ isFullscreen, setFullscreen: setIsFullscreen, toggleFullscreen: toggle }),
    [isFullscreen, toggle],
  );
  return <FullscreenContext.Provider value={value}>{children}</FullscreenContext.Provider>;
}
